import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  FileCheck, 
  AlertTriangle, 
  Lock, 
  Unlock, 
  CreditCard, 
  Clock, 
  CheckCircle2,
  Pen,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== AUTHORIZATION MODAL ====================
export const DS160AuthorizationModal = ({ isOpen, onClose, onAuthorized, familyMembers = [] }) => {
  const [disclaimer, setDisclaimer] = useState('');
  const [signature, setSignature] = useState('');
  const [confirmInfo, setConfirmInfo] = useState(false);
  const [confirmResponsibility, setConfirmResponsibility] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDisclaimer, setLoadingDisclaimer] = useState(true);

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ds160-auth/disclaimer`);
        const data = await res.json();
        setDisclaimer(data.disclaimer);
      } catch (err) {
        console.error('Error fetching disclaimer:', err);
      } finally {
        setLoadingDisclaimer(false);
      }
    };
    if (isOpen) {
      fetchDisclaimer();
    }
  }, [isOpen]);

  const handleAuthorize = async () => {
    if (!signature.trim()) {
      toast.error('Debes escribir tu nombre completo como firma');
      return;
    }
    if (!confirmInfo || !confirmResponsibility || !confirmTerms) {
      toast.error('Debes aceptar todos los términos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const personIds = familyMembers.map(m => m.id);
      
      const res = await fetch(`${API_URL}/api/ds160-auth/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name_signature: signature,
          confirm_information_correct: confirmInfo,
          confirm_responsibility: confirmResponsibility,
          confirm_terms: confirmTerms,
          person_ids: personIds
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al autorizar');

      toast.success('Información autorizada exitosamente');
      onAuthorized && onAuthorized(data);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-amber-500" />
            Autorización de Información DS-160
          </DialogTitle>
          <DialogDescription>
            Por favor lee cuidadosamente y autoriza tu información antes de continuar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Disclaimer Text */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto">
            {loadingDisclaimer ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
              </div>
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                {disclaimer}
              </pre>
            )}
          </div>

          {/* Warning */}
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              <strong>Importante:</strong> Una vez que tu DS-160 sea finalizado (con MRV registrado), 
              cualquier cambio requerirá un pago adicional de <strong>$25 USD por persona</strong>.
            </AlertDescription>
          </Alert>

          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="confirmInfo" 
                checked={confirmInfo}
                onCheckedChange={setConfirmInfo}
                data-testid="confirm-info-checkbox"
              />
              <label htmlFor="confirmInfo" className="text-sm text-slate-700 cursor-pointer">
                Confirmo que <strong>toda la información proporcionada es verdadera y correcta</strong> según mi mejor conocimiento.
              </label>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox 
                id="confirmResponsibility" 
                checked={confirmResponsibility}
                onCheckedChange={setConfirmResponsibility}
                data-testid="confirm-responsibility-checkbox"
              />
              <label htmlFor="confirmResponsibility" className="text-sm text-slate-700 cursor-pointer">
                Entiendo que <strong>soy el único responsable</strong> de la veracidad de los datos y documentos proporcionados.
              </label>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox 
                id="confirmTerms" 
                checked={confirmTerms}
                onCheckedChange={setConfirmTerms}
                data-testid="confirm-terms-checkbox"
              />
              <label htmlFor="confirmTerms" className="text-sm text-slate-700 cursor-pointer">
                Acepto los términos del deslinde de responsabilidad y entiendo las condiciones para modificaciones futuras.
              </label>
            </div>
          </div>

          {/* Digital Signature */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Pen className="h-4 w-4" />
              Firma Digital (escribe tu nombre completo)
            </label>
            <Input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Tu nombre completo como firma"
              className="font-serif text-lg"
              data-testid="signature-input"
            />
            <p className="text-xs text-slate-500">
              Al escribir tu nombre, estás firmando electrónicamente este documento.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAuthorize} 
            disabled={loading || !confirmInfo || !confirmResponsibility || !confirmTerms || !signature.trim()}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="authorize-btn"
          >
            {loading ? 'Autorizando...' : 'Autorizar Información'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== AUTHORIZATION STATUS CARD ====================
export const DS160AuthorizationStatus = ({ onRequestChanges }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ds160-auth/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching DS160 auth status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-3/4"></div>
        </CardContent>
      </Card>
    );
  }

  if (!status?.has_case) return null;

  return (
    <>
      <Card className="border-l-4 border-l-amber-500" data-testid="ds160-auth-status-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5 text-amber-500" />
            Estado de Autorización DS-160
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Authorization Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Autorización:</span>
            {status.authorized ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Autorizado
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                <AlertCircle className="h-3 w-3 mr-1" />
                Pendiente
              </Badge>
            )}
          </div>

          {status.authorized && status.signature && (
            <div className="text-xs text-slate-500">
              Firmado por: <span className="font-medium">{status.signature}</span> el{' '}
              {new Date(status.authorization_date).toLocaleDateString('es-ES')}
            </div>
          )}

          {/* Finalization Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Proceso:</span>
            {status.finalized ? (
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                <Lock className="h-3 w-3 mr-1" />
                Finalizado
              </Badge>
            ) : (
              <Badge variant="outline">
                <Unlock className="h-3 w-3 mr-1" />
                En Proceso
              </Badge>
            )}
          </div>

          {status.mrv_data && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-green-800">MRV Registrado</div>
              <div className="text-green-700">Confirmación: {status.mrv_data.confirmation_number}</div>
              {status.mrv_data.interview_date && (
                <div className="text-green-700">Fecha de entrevista: {status.mrv_data.interview_date}</div>
              )}
            </div>
          )}

          {/* Edit Window */}
          {status.finalized && status.edit_window_remaining_hours && (
            <Alert className="bg-blue-50 border-blue-200">
              <Clock className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Ventana de edición activa:</strong> {status.edit_window_remaining_hours} horas restantes para hacer cambios.
              </AlertDescription>
            </Alert>
          )}

          {/* Requires Payment */}
          {status.requires_payment_to_edit && (
            <Alert className="bg-amber-50 border-amber-200">
              <CreditCard className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Para modificar tu DS-160 debes pagar <strong>${status.modification_price_per_person} USD por persona</strong>.
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            {!status.authorized && (
              <Button 
                onClick={() => setShowAuthModal(true)}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="authorize-btn-card"
              >
                <FileCheck className="h-4 w-4 mr-2" />
                Autorizar Información
              </Button>
            )}

            {status.finalized && status.requires_payment_to_edit && (
              <Button 
                variant="outline"
                onClick={() => setShowChangeModal(true)}
                className="flex-1"
                data-testid="request-changes-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Solicitar Cambios ($25/persona)
              </Button>
            )}
          </div>

          {/* Pending Change Requests */}
          {status.pending_change_requests?.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="text-sm font-medium text-slate-700 mb-2">Solicitudes de Cambio Pendientes:</div>
              {status.pending_change_requests.map((req) => (
                <div key={req.id} className="bg-slate-50 rounded p-2 text-sm">
                  <span className="text-slate-600">{req.num_persons} persona(s) - ${req.amount}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DS160AuthorizationModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthorized={() => {
          fetchStatus();
        }}
      />

      <DS160ChangeRequestModal
        isOpen={showChangeModal}
        onClose={() => setShowChangeModal(false)}
        pricePerPerson={status?.modification_price_per_person || 25}
        onSuccess={() => {
          fetchStatus();
        }}
      />
    </>
  );
};

// ==================== CHANGE REQUEST MODAL ====================
export const DS160ChangeRequestModal = ({ isOpen, onClose, pricePerPerson, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [numPersons, setNumPersons] = useState(1);
  const [loading, setLoading] = useState(false);

  const totalAmount = numPersons * pricePerPerson;

  const handleRequest = async () => {
    if (!reason.trim()) {
      toast.error('Debes indicar el motivo del cambio');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Generate person IDs (for simplicity, just the count matters)
      const personIds = Array.from({ length: numPersons }, (_, i) => `person-${i+1}`);

      const res = await fetch(`${API_URL}/api/ds160-auth/request-changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          person_ids: personIds,
          reason: reason
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al solicitar cambios');

      toast.success(`Link de pago generado. Total: $${data.amount}`);
      
      // Redirect to payment
      if (data.payment_link) {
        window.open(data.payment_link, '_blank');
      }

      onSuccess && onSuccess(data);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-500" />
            Solicitar Modificaciones DS-160
          </DialogTitle>
          <DialogDescription>
            Se requiere un pago de ${pricePerPerson} USD por cada persona para desbloquear la edición.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Número de personas a modificar</label>
            <Input
              type="number"
              min={1}
              max={10}
              value={numPersons}
              onChange={(e) => setNumPersons(Math.max(1, parseInt(e.target.value) || 1))}
              data-testid="num-persons-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Motivo del cambio</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Corrección de fecha de nacimiento"
              data-testid="change-reason-input"
            />
          </div>

          <div className="bg-slate-100 rounded-lg p-4">
            <div className="flex justify-between text-sm">
              <span>Precio por persona:</span>
              <span>${pricePerPerson} USD</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Personas:</span>
              <span>{numPersons}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
              <span>Total:</span>
              <span className="text-amber-600">${totalAmount.toFixed(2)} USD</span>
            </div>
          </div>

          <Alert className="bg-blue-50 border-blue-200">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 text-sm">
              Una vez pagado, tendrás <strong>48 horas</strong> para realizar los cambios.
            </AlertDescription>
          </Alert>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRequest} 
            disabled={loading || !reason.trim()}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="generate-payment-btn"
          >
            {loading ? 'Generando...' : `Pagar $${totalAmount.toFixed(2)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== ADMIN UNLOCK COMPONENT ====================
export const AdminDS160Unlock = ({ caseId, clientName, onUnlocked }) => {
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(48);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleUnlock = async () => {
    if (!reason.trim()) {
      toast.error('Debes indicar el motivo');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ds160-auth/admin/unlock/${caseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: reason,
          unlock_hours: hours
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');

      toast.success(`DS-160 desbloqueado por ${hours} horas`);
      onUnlocked && onUnlocked(data);
      setShowModal(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setShowModal(true)}
        data-testid="admin-unlock-btn"
      >
        <Unlock className="h-4 w-4 mr-1" />
        Desbloquear
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Desbloquear DS-160 sin Cobro</DialogTitle>
            <DialogDescription>
              Desbloquear edición para {clientName || 'este cliente'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Motivo del desbloqueo</label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: Error de nuestro equipo en la captura"
                data-testid="unlock-reason-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Horas de edición</label>
              <Input
                type="number"
                min={1}
                max={168}
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value) || 48)}
              />
            </div>

            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Esta acción no generará cobro al cliente. Solo usar en casos especiales.
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleUnlock} 
              disabled={loading || !reason.trim()}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-unlock-btn"
            >
              {loading ? 'Desbloqueando...' : 'Desbloquear'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DS160AuthorizationStatus;
